import { useEffect, useRef, useState } from "react";
import {
  PageContainer,
  SecondaryContainer as CommonContainer,
  NoContent,
} from "components";
import clsx from "clsx";
import { useLocale } from "hooks";
import Sidebar from "./Sidebar";
import VideoCard from "./VideoCard";
import RelatedConferences from "./RelatedConferences";
import translation, { PLAYING_MODE } from "./translation";
import { loadVideo } from "utils/newFunction";
import Toast from "components/Toast";
import ApiService from "services/client/ApiService";
import { useRouter } from "next/router";

enum PathParams {
  VIDEO_ID,
  VIDEO_SLUG,
}

const clientApiService = new ApiService("fa", true);

function ConferenceVideos({ className, data }) {
  const { isRTL, locale } = useLocale();
  const [activeVideo, setActiveVideo] = useState<Object | null>(
    data?.conference?.videos?.[0] || null
  );
  const [videos, setVideos] = useState<Array<any>>(
    () => data?.conference?.videos
  );
  const [playingMode, setPlayingMode] = useState<PLAYING_MODE>(
    PLAYING_MODE.ORDERED
  );
  const [shareLinkList, setShareLinkList] = useState<
    Map<string, { raw: string; hashed: string }>
  >(() => new Map<string, { raw: string; hashed: string }>());
  const videoPlayerRef = useRef<HTMLVideoElement>(null);
  const router = useRouter();
  const mainSideRef = useRef<HTMLDivElement>(null);

  const getLinkAndUpdateShareLinkList = async (targetItem: any) => {
    if (targetItem) {
      const storedValue = shareLinkList.get(targetItem?.id);
      if (storedValue) {
        return storedValue;
      }

      const raw = `${window.location.protocol}//${window.location.host}${
        locale === "fa" ? "" : "/en"
      }${encodeURI(targetItem?.link)}`;
      let hashed = "";
      try {
        hashed = await clientApiService.getTinyLink(raw);
      } catch (error) {
        console.log(error);
        hashed = raw;
      }
      const result = { raw, hashed };
      setShareLinkList(
        (prev) =>
          new Map<string, { raw: string; hashed: string }>([
            ...prev.entries(),
            [targetItem?.id, result],
          ])
      );
      return result;
    }
    return { raw: "", hashed: "" };
  };

  useEffect(() => {
    (async () => {
      const determinatorList = await Promise.all<
        Array<{ shouldUpdate: boolean; data: any }>
      >(
        data?.conference?.videos?.map?.(async (item) => {
          const defaultValue = {
            data: item,
            shouldUpdate: false,
          };
          if (item?.duration) {
            return defaultValue;
          }
          try {
            const duration = await loadVideo(item?.src);
            return {
              shouldUpdate: true,
              data: { ...item, duration },
            };
          } catch {
            return defaultValue;
          }
        }) || []
      );
      setVideos(determinatorList.map((item) => item?.data));
      determinatorList?.forEach((item) => {
        if (item?.shouldUpdate) {
          clientApiService.updateVideoDuration(item?.data?.id, item?.data);
        }
      });
    })();
  }, [data?.conference?.videos]);

  useEffect(() => {
    const defaultVideo = data?.conference?.videos?.[0] || null;
    const videoId = router.query?.slug?.[PathParams.VIDEO_ID];
    if (videoId) {
      const target =
        videos?.find((item) => String(item.id) === videoId) || defaultVideo;
      setActiveVideo(target);
    } else {
      setActiveVideo(defaultVideo);
    }
  }, [data?.conference?.videos, router.query]);

  const onSidebarItemClick = (item: any) => {
    router.push(item?.link, undefined, { shallow: true });
  };

  const onVideoEnd = () => {
    if (playingMode === PLAYING_MODE.REPEATED) {
      videoPlayerRef?.current?.play?.();
      return;
    }

    const index = videos?.findIndex?.((item) => item?.id === activeVideo?.id);
    if (index === videos?.length - 1) {
      onSidebarItemClick(videos?.[0]);
    } else {
      onSidebarItemClick(videos?.[index + 1]);
    }
  };

  const renderList = () => {
    if (data?.conference?.videos?.length === 0) {
      return <NoContent message={translation[locale]?.EMPTY_LIST} />;
    }
    return (
      <div className="tw-flex tw-flex-col lg:tw-flex-row">
        <div className="tw-mt-4 lg:tw-mt-0 lg:tw-w-[402px] xxxl:tw-w-[502px] tw-order-2 lg:tw-order-1">
          <Sidebar
            videos={videos}
            onSidebarItemClick={onSidebarItemClick}
            activeItem={activeVideo}
            playingMode={playingMode}
            setPlayingMode={setPlayingMode}
            getLinkAndUpdateShareLinkList={getLinkAndUpdateShareLinkList}
            mainSideRef={mainSideRef}
          />
        </div>
        <div
          className={clsx(
            "lg:tw-w-[calc(100%-30px-402px)] xxxl:tw-w-[calc(100%-30px-502px)] tw-order-1 lg:tw-order-2",
            {
              "lg:tw-mr-[30px]": isRTL,
              "lg:tw-ml-[30px]": !isRTL,
            }
          )}
        >
          <VideoCard
            item={activeVideo}
            getLinkAndUpdateShareLinkList={getLinkAndUpdateShareLinkList}
            onVideoEnd={onVideoEnd}
            ref={mainSideRef}
            playerRef={videoPlayerRef}
          />
        </div>
      </div>
    );
  };
  return (
    <PageContainer
      className={className}
      breadCrumb={data?.breadcrumb}
      seo={data?.seo}
      metaTag={Object.assign(data?.metaTag || {}, {
        title: activeVideo?.title || data?.metaTag?.title || "",
      })}
      changeLanguageLink={activeVideo?.changeLanguageLink}
      canonical={!activeVideo?.changeLanguageLink}
      noIndex={activeVideo?.noIndex}
    >
      <Toast />
      <CommonContainer className="tw-px-4 md:tw-px-0 tw-pb-[50px]">
        {renderList()}
      </CommonContainer>
      <RelatedConferences conferences={data?.conferences} />
    </PageContainer>
  );
}

export default ConferenceVideos;
